import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './ScrunchDemo.css';

type FetchResult = {
  final_url: string;
  raw_content: string;
  title: string | null;
  description: string | null;
  status_code: number;
  content_type: string | null;
  duration_ms: number;
  markdown_content?: string | null;
  key_topics?: string[] | null;
  named_entities?: string[] | null;
  llm_provider?: 'foundry' | 'openai' | null;
  llm_model?: string | null;
  simulated?: boolean;
};

type Tab = 'metadata' | 'html' | 'json' | 'insights' | 'markdown';
const API_BASE_URL = (process.env.REACT_APP_SCRUNCH_API_URL || 'http://localhost:8000').replace(/\/$/, '');
const PASSWORD = 'scrunch-takehome';
const SESSION_KEY = 'scrunch-demo-unlocked';

const examples = [
  { label: 'Success', url: 'https://example.com', kind: 'real' },
  { label: 'Redirect', url: 'https://httpbin.org/redirect/1', kind: 'real' },
  { label: '404', url: 'https://demo.scrunch.invalid/not-found', kind: '404' },
  { label: 'Robots denied', url: 'https://demo.scrunch.invalid/private', kind: 'robots' },
  { label: 'Connection failure', url: 'https://demo.scrunch.invalid/offline', kind: 'network' },
  { label: 'Timeout', url: 'https://demo.scrunch.invalid/slow', kind: 'timeout' },
  { label: 'Response too large', url: 'https://demo.scrunch.invalid/too-large', kind: 'too-large' },
  { label: 'Rate limited', url: 'https://demo.scrunch.invalid/rate-limited', kind: 'rate-limit' },
];

function sessionUnlocked() {
  try { return window.sessionStorage.getItem(SESSION_KEY) === 'true'; } catch { return false; }
}

export default function ScrunchDemo() {
  const [unlocked, setUnlocked] = useState(sessionUnlocked);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [url, setUrl] = useState('https://example.com');
  const [userAgent, setUserAgent] = useState('Scrunchbot');
  const [extract, setExtract] = useState(false);
  const [llmAvailable, setLlmAvailable] = useState(false);
  const [llmProviders, setLlmProviders] = useState<Array<'foundry' | 'openai'>>([]);
  const [llmProvider, setLlmProvider] = useState<'foundry' | 'openai'>('foundry');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<FetchResult | null>(null);
  const [tab, setTab] = useState<Tab>('metadata');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/health`)
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        const providers = Array.isArray(data?.llm_providers) ? data.llm_providers : [];
        setLlmProviders(providers);
        setLlmAvailable(Boolean(data?.llm_enabled && providers.length));
        if (data?.default_llm_provider && providers.includes(data.default_llm_provider)) {
          setLlmProvider(data.default_llm_provider);
        } else if (providers.length) setLlmProvider(providers[0]);
      })
      .catch(() => { setLlmAvailable(false); setLlmProviders([]); });
  }, []);

  const formattedJson = useMemo(() => result ? JSON.stringify(result, null, 2) : '', [result]);

  function unlock(event: FormEvent) {
    event.preventDefault();
    if (password === PASSWORD) {
      try { window.sessionStorage.setItem(SESSION_KEY, 'true'); } catch { /* session storage may be unavailable */ }
      setUnlocked(true); setPassword(''); setPasswordError('');
    } else setPasswordError('That password is incorrect. Please try again.');
  }

  async function fetchContent(event?: FormEvent) {
    event?.preventDefault();
    setLoading(true); setError(''); setResult(null); setCopied(false);
    try {
      const params = new URLSearchParams({ url, user_agent: userAgent });
      if (extract) {
        params.set('extract', 'true');
        params.set('llm_provider', llmProvider);
      }
      const response = await fetch(`${API_BASE_URL}/fetch?${params}`);
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error?.message || `The retriever returned HTTP ${response.status}.`);
      setResult(payload); setTab('metadata');
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : '';
      setError(
        message === 'Failed to fetch'
          ? `The Scrunch API could not be reached at ${API_BASE_URL}. Start it locally with npm run start:scrunch, or configure REACT_APP_SCRUNCH_API_URL for the deployed site.`
          : message || 'The backend could not be reached. Is it running on port 8000?',
      );
    } finally { setLoading(false); }
  }

  function runExample(example: typeof examples[number]) {
    setUrl(example.url); setResult(null); setError('');
    if (example.kind === 'real') return;
    const messages: Record<string, string> = {
      '404': 'The requested page was not found (HTTP 404).',
      robots: 'robots.txt does not allow Scrunchbot to fetch this URL.',
      network: 'The destination could not be reached due to a simulated connection failure.',
      timeout: 'The destination did not respond before the simulated timeout.',
      'too-large': 'The response exceeded the 2 MiB download limit (HTTP 413).',
      'rate-limit': 'The per-domain request limit was reached (HTTP 429).',
    };
    setError(`Simulated demo: ${messages[example.kind]}`);
  }

  async function copyJson() {
    await navigator.clipboard.writeText(formattedJson);
    setCopied(true); window.setTimeout(() => setCopied(false), 1600);
  }

  if (!unlocked) return (
    <main className="scrunch-page scrunch-lock-page">
      <section className="scrunch-lock-card">
        <div className="scrunch-mark" aria-hidden="true">S</div>
        <p className="scrunch-eyebrow">PRIVATE ENGINEERING DEMO</p>
        <h1>Web Content Retriever</h1>
        <p>Enter the take-home password to access the interactive Scrunch demo.</p>
        <form onSubmit={unlock}>
          <label htmlFor="scrunch-password">Demo password</label>
          <input id="scrunch-password" type="password" autoFocus autoComplete="current-password" value={password} onChange={(event) => { setPassword(event.target.value); setPasswordError(''); }} />
          {passwordError && <p className="scrunch-field-error" role="alert">{passwordError}</p>}
          <button className="scrunch-primary" type="submit">Access demo <span aria-hidden="true">→</span></button>
        </form>
      </section>
    </main>
  );

  return (
    <main className="scrunch-page">
      <header className="scrunch-header">
        <a href="/" className="scrunch-brand"><span className="scrunch-mark">S</span><span>SCRUNCH</span></a>
        <div className="scrunch-header-meta"><span className="scrunch-live-dot" />Interactive backend demo</div>
      </header>

      <div className="scrunch-shell">
        <section className="scrunch-intro">
          <p className="scrunch-eyebrow">ENGINEERING TAKE-HOME</p>
          <h1>Web Content Retriever</h1>
          <p>Fetch any public web page and inspect its source, metadata, redirect destination, and structured API response.</p>
          <Link className="scrunch-explainer-link" to="/scrunch-demo/explainer">
            <span aria-hidden="true">◎</span>
            <span><strong>How does this system work?</strong><small>Open the visual architecture explainer</small></span>
            <b aria-hidden="true">→</b>
          </Link>
        </section>

        <section className="scrunch-workbench">
          <form className="scrunch-fetch-card" onSubmit={fetchContent}>
            <div className="scrunch-card-heading"><div><span>01</span><h2>Request</h2></div><span className="scrunch-method">GET /fetch</span></div>
            <label htmlFor="scrunch-url">URL</label>
            <div className="scrunch-url-row"><span aria-hidden="true">↗</span><input id="scrunch-url" required type="url" placeholder="https://example.com" value={url} onChange={(event) => setUrl(event.target.value)} /></div>
            <div className="scrunch-form-row">
              <label htmlFor="scrunch-agent">User-Agent <small>optional</small></label>
              <input id="scrunch-agent" value={userAgent} onChange={(event) => setUserAgent(event.target.value)} />
            </div>
            <label className={`scrunch-toggle ${!llmAvailable ? 'disabled' : ''}`}><input type="checkbox" checked={extract} disabled={!llmAvailable} onChange={(event) => setExtract(event.target.checked)} /><span /><div><strong>Extract with AI</strong><small>{llmAvailable ? 'Topics and named entities' : 'Not enabled on this backend'}</small></div></label>
            {extract && llmAvailable && <label className="scrunch-provider-field" htmlFor="scrunch-provider">AI provider<select id="scrunch-provider" value={llmProvider} onChange={(event) => setLlmProvider(event.target.value as 'foundry' | 'openai')}>{llmProviders.map((provider) => <option value={provider} key={provider}>{provider === 'foundry' ? 'Microsoft Foundry · GPT-5 nano' : 'OpenAI API'}</option>)}</select><small>Only providers configured on the server are shown.</small></label>}
            <button className="scrunch-primary scrunch-fetch" disabled={loading} type="submit">{loading ? <><i className="scrunch-spinner" /> Fetching content…</> : <>Fetch content <span>→</span></>}</button>
          </form>

          <aside className="scrunch-examples">
            <div className="scrunch-card-heading"><div><span>02</span><h2>Test scenarios</h2></div></div>
            <p>Choose a scenario, then fetch real examples or inspect deterministic simulated failures.</p>
            <div className="scrunch-example-list">{examples.map((example) => <button type="button" key={example.label} onClick={() => runExample(example)}><span className={`scrunch-example-icon ${example.kind === 'real' ? 'ok' : ''}`}>{example.kind === 'real' ? '↗' : '!'}</span><span><strong>{example.label}</strong><small>{example.kind === 'real' ? 'Live request' : 'Simulated response'}</small></span><b>›</b></button>)}</div>
          </aside>
        </section>

        {error && <section className="scrunch-error" role="alert"><span>!</span><div><strong>Request unsuccessful</strong><p>{error}</p></div></section>}

        <section className={`scrunch-results ${result ? 'has-result' : ''}`} aria-live="polite">
          <div className="scrunch-card-heading"><div><span>03</span><h2>Response</h2></div>{result && <span className="scrunch-response-status"><i /> {result.simulated ? 'SIMULATED' : 'LIVE'} · {result.status_code} OK</span>}</div>
          {!result ? <div className="scrunch-empty"><div>⌁</div><h3>Ready for a request</h3><p>Enter a public URL above to inspect the retrieved content.</p></div> : <>
            <div className="scrunch-overview">
              <div><span>STATUS</span><strong className="scrunch-green">{result.status_code} OK</strong></div>
              <div><span>DURATION</span><strong>{result.duration_ms} ms</strong></div>
              <div><span>CONTENT TYPE</span><strong>{result.content_type?.split(';')[0] || 'unknown'}</strong></div>
              <div><span>SIZE</span><strong>{new Blob([result.raw_content]).size.toLocaleString()} B</strong></div>
            </div>
            <div className="scrunch-tabs" role="tablist">
              {([['metadata', 'Metadata'], ['html', 'Raw HTML'], ['json', 'JSON response'], ...(result.key_topics ? [['insights', 'AI insights']] : []), ...(result.markdown_content ? [['markdown', 'Markdown']] : [])] as [Tab, string][]).map(([id, label]) => <button role="tab" aria-selected={tab === id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)} key={id}>{label}</button>)}
            </div>
            <div className="scrunch-tab-panel">
              {tab === 'metadata' && <dl className="scrunch-metadata"><div><dt>Final URL</dt><dd><a href={result.final_url} target="_blank" rel="noreferrer">{result.final_url}</a></dd></div><div><dt>Page title</dt><dd>{result.title || <em>Not provided</em>}</dd></div><div><dt>Meta description</dt><dd>{result.description || <em>Not provided</em>}</dd></div></dl>}
              {tab === 'html' && <pre><code>{result.raw_content}</code></pre>}
              {tab === 'json' && <div className="scrunch-code-wrap"><button type="button" onClick={copyJson}>{copied ? 'Copied!' : 'Copy JSON'}</button><pre><code>{formattedJson}</code></pre></div>}
              {tab === 'insights' && <div className="scrunch-insights-wrap"><p>Generated by <strong>{result.llm_provider === 'foundry' ? 'Microsoft Foundry' : 'OpenAI'}</strong>{result.llm_model ? ` · ${result.llm_model}` : ''}</p><div className="scrunch-insights"><div><h3>Key topics</h3>{result.key_topics?.map((topic) => <span key={topic}>{topic}</span>)}</div><div><h3>Named entities</h3>{result.named_entities?.map((entity) => <span key={entity}>{entity}</span>)}</div></div></div>}
              {tab === 'markdown' && <pre className="scrunch-markdown"><code>{result.markdown_content}</code></pre>}
            </div>
          </>}
        </section>
      </div>
      <footer className="scrunch-footer"><span>Prapti Setty</span></footer>
    </main>
  );
}
